import React, { createContext } from 'react';
import Head from 'next/head';
import BasePage from '@sm/webassets/BasePage';
import { FourOhFourErrorPage } from '@sm/webassets/FourOhFourError';
import { SurveyThemeProvider } from '@sm/webassets/SurveyTheme/context';
import { SurveyTheme as SurveyThemeType } from '@sm/webassets/SurveyTheme/types';
import { Box } from '@wds/box';
import { t } from '@sm/intl';

import { Header, EnterpriseThankYouBody, EnterpriseThankYouFooter, EnterpriseThankYouLayout } from './components';
import COPY from './copy';
import useStyles from './useStyles';
import { GetDefaultEndPageSettingsQuery } from '~lib/generatedGqlTypes';
import { PageOptions } from '~helpers/pages/types';

export const PageVariants = {
  standard: Symbol('standard'),
  premiumStandard: Symbol('premium_standard'),
  premiumCustom: Symbol('premium_custom'),
};
export const EnterpriseThankYouContext = createContext<{ pageVariant?: symbol }>({});

const determinePageVariant = (isStandard: boolean, isPremiumCustomizable: boolean): symbol => {
  if (isStandard) {
    return PageVariants.standard;
  }
  if (isPremiumCustomizable) {
    return PageVariants.premiumCustom;
  }
  return PageVariants.premiumStandard;
};

export type EnterpriseThankYouComponentProps = {
  defaultEndPageSettings?: GetDefaultEndPageSettingsQuery['defaultEndPageSettings'] | null;
  premiumCustomFontLink: string | null;
  surveyTheme: SurveyThemeType | null;
  pageOptions?: PageOptions;
  isDataFetchError: boolean;
};

function EnterpriseThankYouComponent({
  defaultEndPageSettings,
  premiumCustomFontLink,
  surveyTheme,
  pageOptions,
  isDataFetchError,
}: EnterpriseThankYouComponentProps): JSX.Element | null {
  const { outline, contentOutline } = useStyles();
  if (isDataFetchError) {
    return <FourOhFourErrorPage />;
  }
  if (!defaultEndPageSettings || !pageOptions || !surveyTheme) {
    return null;
  }

  const { hasStandard, canonical = '' } = pageOptions;

  const customEndPageIsEnabled = defaultEndPageSettings?.isEnabled; // Used to distinguish between the "custom" and "standard" premium pages, which is different from standard/premium vars taken from smParam
  const headerLogoUrl = defaultEndPageSettings?.logo?.url;
  const headerText = defaultEndPageSettings?.thankYouText;
  const bodyImageUrl = defaultEndPageSettings?.image?.url;
  const bodyText = defaultEndPageSettings?.bodyText;
  const bodyImageRedirectUrl = defaultEndPageSettings?.imageRedirectUrl;
  const pageVariant = determinePageVariant(hasStandard, customEndPageIsEnabled);

  return (
    <BasePage pageId="EnterpriseThankYou" legacyWeb="respweb" includeHeader={false} includeFooter={false}>
      <Head>
        <base target="_top" href="/" />
        <title>{t(COPY.THANK_YOU)}</title>
        <link rel="canonical" href={canonical} />
        {premiumCustomFontLink && <link rel="stylesheet" href={premiumCustomFontLink} />}
      </Head>
      <SurveyThemeProvider theme={surveyTheme}>
        <EnterpriseThankYouContext.Provider value={{ pageVariant }}>
          <EnterpriseThankYouLayout>
            <Box className={outline} flexJustify="center" py={9} data-testid="EnterpriseThankYou__OutlineContainer">
              <Box className={contentOutline}>
                <Header headerLogoUrl={headerLogoUrl} headerText={headerText} />
                <EnterpriseThankYouBody
                  bodyImageUrl={bodyImageUrl}
                  bodyText={bodyText}
                  bodyImageRedirectUrl={bodyImageRedirectUrl}
                />
              </Box>
              <EnterpriseThankYouFooter />
            </Box>
          </EnterpriseThankYouLayout>
        </EnterpriseThankYouContext.Provider>
      </SurveyThemeProvider>
    </BasePage>
  );
}

export default EnterpriseThankYouComponent;
